import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BRAND, STOCK } from '@/lib/constants';

const SITE_URL = (typeof window !== 'undefined' ? window.location.origin : 'https://floridasunkissedmangoes.com');

export default function Seo({
  title,
  description,
  keywords,
  ogImage,
  type = 'website',
  noIndex = false,
}) {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;
  const fullTitle = title ? `${title} — ${BRAND.name}` : `${BRAND.name} — ${BRAND.tagline}`;
  const desc = description || `${BRAND.tagline} ${BRAND.footerTagline}`;
  const img = ogImage || STOCK.mango_closeup;

  useEffect(() => {
    // Ensure title is set immediately even before Helmet flushes (helpful on first paint)
    document.title = fullTitle;
  }, [fullTitle]);

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} />
      <meta name="theme-color" content="#FF7A1A" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}

export function OrganizationJsonLd() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND.name,
    url: SITE_URL,
    logo: BRAND.logos.primary,
    description: BRAND.tagline,
    parentOrganization: {
      '@type': 'Organization',
      name: 'Coastal Capital & AgriCo',
    },
    contactPoint: [{
      '@type': 'ContactPoint',
      telephone: BRAND.contact.phone,
      email: BRAND.contact.email,
      contactType: 'customer service',
      areaServed: 'US',
    }],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'West Palm Beach',
      addressRegion: 'FL',
      addressCountry: 'US',
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}
